<p-dialog
  [(visible)]="visible"
  [draggable]="false"
  [resizable]="false"
  [modal]="true"
  [closable]="true"
  [header]="dialogTitle"
  [appendTo]="'body'"
  [styleClass]="'product-dialog'"
>
  <ng-template pTemplate="header">
    <div class="dialog-header" [attr.data-subtitle]="isEditing ? ('productsCrud.dialogSubtitle.edit' | transloco) : ('productsCrud.dialogSubtitle.new' | transloco)">
      <span class="dialog-title">{{ dialogTitle }}</span>
    </div>
  </ng-template>

  <form [formGroup]="productForm" (ngSubmit)="onSubmit()" (keydown.enter)="submitOnEnterSafe($event)" class="form-container">
    <div class="form-group">
      <!-- Product -->
      <section class="form-section">
        <h4 class="section-title">{{ 'productsCrud.sections.product' | transloco }}</h4>

        <div class="form-field">
          <app-input
            [label]="'productsCrud.fields.productName' | transloco"
            [placeholder]="'productsCrud.fields.productNamePlaceholder' | transloco"
            formControlName="name"
            [required]="true"
            [error]="!!productForm.get('name')?.invalid && !!productForm.get('name')?.touched"
            [errorMessage]="getErrorMessage('name') || ''"
            size="medium"
          ></app-input>
        </div>

        <!-- Two short numbers: side by side also on a phone -->
        <div class="form-row form-row--pair">
          <div class="form-field">
            <app-input
              [label]="'productsCrud.fields.minAmount' | transloco"
              [placeholder]="'productsCrud.fields.minAmountPlaceholder' | transloco"
              formControlName="minAmount"
              type="number"
              [error]="isFieldInvalid('minAmount')"
              [errorMessage]="getErrorMessage('minAmount') || ''"
              size="medium"
            ></app-input>
          </div>

          <div class="form-field">
            <app-input
              [label]="'productsCrud.fields.maxAmount' | transloco"
              [placeholder]="'productsCrud.fields.maxAmountPlaceholder' | transloco"
              formControlName="maxAmount"
              type="number"
              [error]="isFieldInvalid('maxAmount')"
              [errorMessage]="getErrorMessage('maxAmount') || ''"
              size="medium"
            ></app-input>
          </div>
        </div>

        <div class="form-row">
          <div class="form-field">
            <app-select
              [label]="'productsCrud.fields.platform' | transloco"
              [placeholder]="'productsCrud.fields.platformPlaceholder' | transloco"
              formControlName="platformId"
              [required]="true"
              [options]="platformOptions"
              [error]="!!productForm.get('platformId')?.invalid && !!productForm.get('platformId')?.touched"
              [errorMessage]="getErrorMessage('platformId') || ''"
              size="medium"
              [appendTo]="null"
            ></app-select>
          </div>

          <div class="form-field">
            <app-input
              [label]="'productsCrud.fields.averageTime' | transloco"
              [placeholder]="'productsCrud.fields.averageTimePlaceholder' | transloco"
              formControlName="averageTime"
              type="number"
              [error]="isFieldInvalid('averageTime')"
              [errorMessage]="getErrorMessage('averageTime') || ''"
              size="medium"
            ></app-input>
          </div>
        </div>

        <div class="toggle-row">
          <div class="toggle-text">
            <span class="toggle-label">{{ 'productsCrud.fields.requiresComments' | transloco }}</span>
            <small class="toggle-description">{{ 'productsCrud.fields.requiresCommentsDescription' | transloco }}</small>
          </div>
          <app-toggle-switch formControlName="requiresCustomComments" size="small"></app-toggle-switch>
        </div>
      </section>

      <!-- Amounts and prices: one compact row per tier, column titles once -->
      <section class="form-section">
        <div class="section-header">
          <h4 class="section-title">{{ 'productsCrud.fields.amountsAndPrices' | transloco }}</h4>
          <button type="button" class="add-button" (click)="addPriceTier()">
            <lucide-icon name="Plus" [size]="16"></lucide-icon>
            <span>{{ 'productsCrud.add' | transloco }}</span>
          </button>
        </div>

        <div formArrayName="priceTiers" class="price-tiers-list">
          <div class="tier-head">
            <span>{{ 'productsCrud.fields.quantity' | transloco }}</span>
            <span>{{ 'productsCrud.fields.priceEur' | transloco }}</span>
            <span></span>
          </div>
          @for (tier of priceTiersArray.controls; track tier; let i = $index) {
            <div [formGroupName]="i" class="tier-row">
              <app-input
                [placeholder]="'productsCrud.fields.quantityPlaceholder' | transloco"
                formControlName="amount"
                type="number"
                size="medium"
                [error]="!!rowError(priceTiersArray, i, 'amount')"
                [errorMessage]="rowError(priceTiersArray, i, 'amount')"
              ></app-input>
              <app-input
                [placeholder]="'productsCrud.fields.pricePlaceholder' | transloco"
                formControlName="price"
                type="number"
                size="medium"
                [error]="!!rowError(priceTiersArray, i, 'price')"
                [errorMessage]="rowError(priceTiersArray, i, 'price')"
              ></app-input>
              <button
                type="button"
                class="icon-action delete"
                [disabled]="priceTiersArray.length <= 1"
                [attr.aria-label]="'productsCrud.aria.removeTier' | transloco"
                (click)="removePriceTier(i)"
              >
                <lucide-icon name="Trash2" [size]="18"></lucide-icon>
              </button>
            </div>
          }
        </div>
      </section>

      <!-- Providers -->
      <section class="form-section">
        <div class="section-header">
          <h4 class="section-title">{{ 'productsCrud.sections.providers' | transloco }}</h4>
          <button type="button" class="add-button" (click)="addProvider()">
            <lucide-icon name="Plus" [size]="16"></lucide-icon>
            <span>{{ 'productsCrud.add' | transloco }}</span>
          </button>
        </div>

        <div formArrayName="providers" class="providers-list">
          @for (provider of providersArray.controls; track provider; let i = $index) {
            <div [formGroupName]="i" class="provider-card" [class.provider-card--active]="provider.get('isActive')?.value">
              <app-select
                [label]="'productsCrud.fields.provider' | transloco"
                [placeholder]="'productsCrud.fields.providerPlaceholder' | transloco"
                formControlName="providerId"
                [required]="true"
                [options]="providerOptions"
                size="medium"
                [appendTo]="null"
                [error]="!!rowError(providersArray, i, 'providerId')"
                [errorMessage]="rowError(providersArray, i, 'providerId')"
              ></app-select>

              <!-- Code + fetch the service data from the provider -->
              <div class="provider-code-row">
                <app-input
                  [label]="'productsCrud.fields.code' | transloco"
                  [placeholder]="'productsCrud.fields.codePlaceholder' | transloco"
                  formControlName="code"
                  [required]="true"
                  size="medium"
                  [error]="!!rowError(providersArray, i, 'code')"
                  [errorMessage]="rowError(providersArray, i, 'code')"
                ></app-input>
                <button
                  type="button"
                  class="fetch-button"
                  [title]="'productsCrud.fields.autocomplete' | transloco"
                  [disabled]="!canFetchService(i) || fetchingService[i]"
                  (click)="fetchProviderService(i)"
                >
                  @if (fetchingService[i]) {
                    <span class="fetch-spinner"></span>
                  } @else {
                    <lucide-icon name="WandSparkles" [size]="20"></lucide-icon>
                  }
                  <span class="fetch-label">{{ 'productsCrud.fields.autocomplete' | transloco }}</span>
                </button>
              </div>

              <div class="provider-footer">
                <button type="button" class="active-chip" [class.active]="provider.get('isActive')?.value" (click)="setActiveProvider(i)">
                  <span class="radio-dot">
                    @if (provider.get('isActive')?.value) {
                      <lucide-icon name="Check" [size]="12"></lucide-icon>
                    }
                  </span>
                  {{ 'productsCrud.fields.active' | transloco }}
                </button>
                <button
                  type="button"
                  class="icon-action delete"
                  [disabled]="providersArray.length <= 1"
                  [attr.aria-label]="'productsCrud.aria.removeProvider' | transloco"
                  (click)="removeProvider(i)"
                >
                  <lucide-icon name="Trash2" [size]="18"></lucide-icon>
                </button>
              </div>
            </div>
          }
        </div>
      </section>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">
      <app-button
        [label]="actionButtonLabel"
        type="submit"
        size="medium"
        [disabled]="isLoading"
        [loading]="isLoading"
        class="submit-button"
      ></app-button>
    </div>
  </form>
</p-dialog>
